/**
 * Sentry Integration for CultStore - Comprehensive Error Tracking
 * Tracks all checkout, login, and API errors with detailed context
 * Version: 2.0 - Enhanced with complete flow coverage
 */

window.CultStoreSentry = (function () {
  let initialized = false;
  let currentUser = null;
  let sessionContext = {};
  
  // Error taxonomy for classification
  const ERROR_CATEGORIES = {
    CHECKOUT: 'checkout',
    LOGIN: 'login',
    API: 'api',
    SDK: 'sdk',
    VALIDATION: 'validation',
    SILENT_FAILURE: 'silent_failure',
    NETWORK: 'network',
    SESSION: 'session',
    PAYMENT: 'payment'
  };
  
  // Error severity levels
  const ERROR_LEVELS = {
    CRITICAL: 'error',     // Blocks user flow completely
    HIGH: 'warning',       // Significant degradation
    MEDIUM: 'warning',     // Partial functionality affected
    LOW: 'info',           // Minor issue, flow continues
    DEBUG: 'debug'         // Debug information
  };

  /**
   * Initialize Sentry with user context
   */
  function init() {
    if (initialized || !window.Sentry || !window.Sentry.setUser) {
      return;
    }

    currentUser = getUserData();

    if (currentUser && currentUser.userId) {
      window.Sentry.setUser({
        id: currentUser.userId,
        cfUserId: currentUser.cfUserId,
        platformUserId: currentUser.platformUserId,
        email: currentUser.email,
        username: currentUser.userName,
        phone: currentUser.phone,
        isInternalUser: currentUser.isInternalUser,
      });
    }

    if (window.Sentry.setTag) {
      window.Sentry.setTag('environment', 'production');
      window.Sentry.setTag('service', 'cultstore-theme');
      window.Sentry.setTag('page_type', getPageType());
    }
    
    // Initialize session context
    updateSessionContext();
    
    initialized = true;
    console.log('[Sentry] Initialized with user context');
  }
  
  /**
   * Get page type for context
   */
  function getPageType() {
    const url = window.location.href;
    if (url.includes('/products/')) return 'product';
    if (url.includes('/collections/')) return 'collection';
    if (url.includes('/cart')) return 'cart';
    if (url.includes('/checkout')) return 'checkout';
    if (url.includes('/account')) return 'account';
    if (url === window.location.origin + '/') return 'homepage';
    return 'other';
  }
  
  /**
   * Update session context with cart and device info
   */
  function updateSessionContext() {
    try {
      sessionContext = {
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
        pageType: getPageType(),
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        deviceId: getCookie('_shopify_y') || 'unknown',
        isWebview: detectWebview(),
        isApp: localStorage.getItem('isApp') === 'true',
      };
      
      // Add cart context if available
      try {
        const cart = window.cart || JSON.parse(localStorage.getItem('cart'));
        if (cart) {
          sessionContext.cart = {
            itemCount: cart.item_count || 0,
            totalPrice: cart.total_price || 0,
            currency: cart.currency || 'INR',
            hasItems: (cart.items || []).length > 0
          };
        }
      } catch (e) {
        // Cart data unavailable
      }
    } catch (error) {
      console.error('[Sentry] Error updating session context:', error);
    }
  }
  
  /**
   * Detect if running in webview
   */
  function detectWebview() {
    const ua = navigator.userAgent || '';
    return /wv|WebView|CriOS|FxiOS/.test(ua) || 
           !!window.ReactNativeWebView ||
           navigator.standalone === false;
  }
  
  /**
   * Get cookie value
   */
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * Get user data from localStorage
   */
  function getUserData() {
    try {
      const customer = JSON.parse(localStorage.getItem('customer'));
      if (!customer) return null;

      return {
        userId: customer?.metafields?.cult?.cfuserid || customer?.id || null,
        cfUserId: customer?.metafields?.cult?.cfuserid || null,
        platformUserId: customer?.id || null,
        email: customer?.metafields?.cult?.email || customer?.email || '',
        phone: customer?.phone || '',
        userName: `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim(),
        isInternalUser: customer?.metafields?.cult?.is_internal_user || false,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Update user context
   */
  function updateUserContext() {
    if (!window.Sentry || !window.Sentry.setUser) return;

    currentUser = getUserData();
    if (currentUser && currentUser.userId) {
      window.Sentry.setUser({
        id: currentUser.userId,
        cfUserId: currentUser.cfUserId,
        platformUserId: currentUser.platformUserId,
        email: currentUser.email,
        username: currentUser.userName,
        phone: currentUser.phone,
        isInternalUser: currentUser.isInternalUser,
      });
    }
    
    // Also update session context
    updateSessionContext();
  }

  /**
   * Capture checkout error with context
   */
  function captureCheckoutError(error, context = {}) {
    if (!window.Sentry || !window.Sentry.captureException) {
      console.error('[Sentry] Not initialized:', error);
      return;
    }

    if (!currentUser) {
      updateUserContext();
    }

    // Add cart data
    let cartData = null;
    try {
      const cart = window.cart || JSON.parse(localStorage.getItem('cart'));
      if (cart) {
        cartData = {
          itemCount: cart.item_count,
          totalPrice: cart.total_price,
          currency: cart.currency || 'INR',
          items: cart.items?.map(item => ({
            id: item.id,
            title: item.title,
            quantity: item.quantity,
            price: item.price,
            sku: item.sku
          })) || [],
        };
      }
    } catch (e) {
      // Ignore
    }

    const enrichedContext = {
      tags: {
        error_type: 'CHECKOUT_TRACKER',
        error_category: ERROR_CATEGORIES.CHECKOUT,
        checkout_stage: context.stage || 'unknown',
        user_id: currentUser?.userId || 'guest',
        has_phone: !!currentUser?.phone,
        is_webview: sessionContext.isWebview ? 'true' : 'false',
      },
      extra: {
        ...context,
        cart: cartData,
        session: sessionContext,
        timestamp: new Date().toISOString(),
      },
    };

    window.Sentry.captureException(error, enrichedContext);
  }
  
  /**
   * Capture login error with context
   */
  function captureLoginError(error, context = {}) {
    if (!window.Sentry || !window.Sentry.captureException) {
      console.error('[Sentry] Not initialized:', error);
      return;
    }
    
    const enrichedContext = {
      tags: {
        error_type: 'LOGIN_ERROR',
        error_category: ERROR_CATEGORIES.LOGIN,
        login_stage: context.stage || 'unknown',
        login_method: context.method || 'unknown',
        is_webview: sessionContext.isWebview ? 'true' : 'false',
      },
      extra: {
        ...context,
        session: sessionContext,
        timestamp: new Date().toISOString(),
      },
      level: context.level || ERROR_LEVELS.HIGH,
    };
    
    window.Sentry.captureException(error, enrichedContext);
  }
  
  /**
   * Capture API error with context
   */
  function captureAPIError(error, context = {}) {
    if (!window.Sentry || !window.Sentry.captureException) {
      console.error('[Sentry] Not initialized:', error);
      return;
    }
    
    const enrichedContext = {
      tags: {
        error_type: 'API_ERROR',
        error_category: ERROR_CATEGORIES.API,
        api_endpoint: context.endpoint || 'unknown',
        http_status: context.status || 'unknown',
        request_method: context.method || 'unknown',
      },
      extra: {
        ...context,
        session: sessionContext,
        user: currentUser,
        timestamp: new Date().toISOString(),
      },
      level: context.level || ERROR_LEVELS.HIGH,
    };
    
    window.Sentry.captureException(error, enrichedContext);
  }
  
  /**
   * Capture SDK error (Breeze/Blaze SDK issues)
   */
  function captureSDKError(error, context = {}) {
    if (!window.Sentry || !window.Sentry.captureException) {
      console.error('[Sentry] Not initialized:', error);
      return;
    }
    
    const enrichedContext = {
      tags: {
        error_type: 'SDK_ERROR',
        error_category: ERROR_CATEGORIES.SDK,
        sdk_name: context.sdkName || 'unknown',
        sdk_available: context.sdkAvailable ? 'true' : 'false',
      },
      extra: {
        ...context,
        session: sessionContext,
        hasBreezeSDK: typeof window.Breeze !== 'undefined',
        hasBlazeSDK: typeof window.BlazeSDK !== 'undefined',
        hasBreezeCheckout: typeof window.breezeCheckout !== 'undefined',
        timestamp: new Date().toISOString(),
      },
      level: context.level || ERROR_LEVELS.CRITICAL,
    };
    
    window.Sentry.captureException(error, enrichedContext);
  }
  
  /**
   * Capture silent failure (things that don't work but don't throw errors)
   */
  function captureSilentFailure(message, context = {}) {
    if (!window.Sentry || !window.Sentry.captureMessage) {
      console.error('[Sentry] Not initialized:', message);
      return;
    }
    
    updateSessionContext(); // Refresh session context for silent failures
    
    const enrichedContext = {
      tags: {
        error_type: 'SILENT_FAILURE',
        error_category: ERROR_CATEGORIES.SILENT_FAILURE,
        failure_type: context.type || 'unknown',
        component: context.component || 'unknown',
      },
      extra: {
        ...context,
        session: sessionContext,
        user: currentUser,
        timestamp: new Date().toISOString(),
      },
      level: context.level || ERROR_LEVELS.MEDIUM,
    };
    
    window.Sentry.captureMessage(message, enrichedContext);
  }
  
  /**
   * Capture validation error
   */
  function captureValidationError(error, context = {}) {
    if (!window.Sentry || !window.Sentry.captureException) {
      console.error('[Sentry] Not initialized:', error);
      return;
    }
    
    const enrichedContext = {
      tags: {
        error_type: 'VALIDATION_ERROR',
        error_category: ERROR_CATEGORIES.VALIDATION,
        field_name: context.field || 'unknown',
        validation_type: context.validationType || 'unknown',
      },
      extra: {
        ...context,
        session: sessionContext,
        timestamp: new Date().toISOString(),
      },
      level: context.level || ERROR_LEVELS.LOW,
    };
    
    window.Sentry.captureException(error, enrichedContext);
  }
  
  /**
   * Capture network/timeout error
   */
  function captureNetworkError(error, context = {}) {
    if (!window.Sentry || !window.Sentry.captureException) {
      console.error('[Sentry] Not initialized:', error);
      return;
    }
    
    const enrichedContext = {
      tags: {
        error_type: 'NETWORK_ERROR',
        error_category: ERROR_CATEGORIES.NETWORK,
        network_type: navigator.connection?.effectiveType || 'unknown',
        is_timeout: context.isTimeout ? 'true' : 'false',
      },
      extra: {
        ...context,
        session: sessionContext,
        connection: {
          type: navigator.connection?.type,
          effectiveType: navigator.connection?.effectiveType,
          downlink: navigator.connection?.downlink,
          rtt: navigator.connection?.rtt,
        },
        timestamp: new Date().toISOString(),
      },
      level: context.level || ERROR_LEVELS.HIGH,
    };
    
    window.Sentry.captureException(error, enrichedContext);
  }

  /**
   * Add breadcrumb
   */
  function addBreadcrumb(message, category = 'user-action', data = {}) {
    if (!window.Sentry || !window.Sentry.addBreadcrumb) return;

    window.Sentry.addBreadcrumb({
      message: message,
      category: category,
      level: 'info',
      data: {
        ...data,
        timestamp: new Date().toISOString(),
      },
    });
  }
  
  /**
   * Get flattened error data for analytics (MoEngage/DataLake)
   * Returns flat object with no nested properties
   */
  function getFlattenedErrorData(errorCode, context = {}) {
    const user = currentUser || {};
    const session = sessionContext || {};
    
    // Start with error basics
    const flattened = {
      errorCode: errorCode || 'UNKNOWN',
      errorMessage: context.message || context.errorMessage || '',
      errorCategory: context.category || ERROR_CATEGORIES.SILENT_FAILURE,
      errorLevel: context.level || ERROR_LEVELS.MEDIUM,
      errorTimestamp: new Date().toISOString(),
    };
    
    // Add user data (flattened)
    flattened.userId = user.userId || 'guest';
    flattened.userCfUserId = user.cfUserId || '';
    flattened.userEmail = user.email || '';
    flattened.userPhone = user.phone || '';
    flattened.userIsInternal = user.isInternalUser || false;
    
    // Add session data (flattened)
    flattened.sessionPageUrl = session.pageUrl || window.location.href;
    flattened.sessionPageType = session.pageType || getPageType();
    flattened.sessionReferrer = session.referrer || document.referrer;
    flattened.sessionDeviceId = session.deviceId || '';
    flattened.sessionIsWebview = session.isWebview || false;
    flattened.sessionIsApp = session.isApp || false;
    flattened.sessionUserAgent = session.userAgent || navigator.userAgent;
    flattened.sessionScreenSize = session.screenSize || '';
    flattened.sessionViewport = session.viewport || '';
    
    // Add cart data (flattened)
    if (session.cart) {
      flattened.cartItemCount = session.cart.itemCount || 0;
      flattened.cartTotalPrice = session.cart.totalPrice || 0;
      flattened.cartCurrency = session.cart.currency || 'INR';
      flattened.cartHasItems = session.cart.hasItems || false;
    } else {
      flattened.cartItemCount = 0;
      flattened.cartTotalPrice = 0;
      flattened.cartCurrency = 'INR';
      flattened.cartHasItems = false;
    }
    
    // Add context-specific fields (flattened)
    if (context.stage) flattened.checkoutStage = context.stage;
    if (context.method) flattened.loginMethod = context.method;
    if (context.endpoint) flattened.apiEndpoint = context.endpoint;
    if (context.status) flattened.httpStatus = context.status;
    if (context.sdkName) flattened.sdkName = context.sdkName;
    if (context.component) flattened.componentName = context.component;
    if (context.field) flattened.fieldName = context.field;
    if (context.validationType) flattened.validationType = context.validationType;
    if (context.isTimeout !== undefined) flattened.isTimeout = context.isTimeout;
    if (context.attempt !== undefined) flattened.attemptNumber = context.attempt;
    if (context.retry !== undefined) flattened.retryNumber = context.retry;
    
    // Add any additional flat context fields
    Object.keys(context).forEach(key => {
      // Skip already handled fields and nested objects
      if (!flattened.hasOwnProperty(key) && 
          typeof context[key] !== 'object' && 
          context[key] !== null) {
        flattened['ctx_' + key] = context[key];
      }
    });
    
    return flattened;
  }

  // Initialize when Sentry loads (with retry logic)
  if (window.Sentry && window.Sentry.setUser) {
    init();
  } else {
    // Retry with delays to ensure Sentry SDK is fully loaded
    setTimeout(init, 500);
    setTimeout(init, 1500);
    setTimeout(init, 3000);
  }

  // Update user context on storage change
  window.addEventListener('storage', function (e) {
    if (e.key === 'customer') {
      updateUserContext();
    }
  });
  
  // Update session context on navigation
  window.addEventListener('popstate', updateSessionContext);
  window.addEventListener('pushstate', updateSessionContext);

  return {
    init,
    updateUserContext,
    updateSessionContext,
    captureCheckoutError,
    captureLoginError,
    captureAPIError,
    captureSDKError,
    captureSilentFailure,
    captureValidationError,
    captureNetworkError,
    addBreadcrumb,
    getFlattenedErrorData,
    ERROR_CATEGORIES,
    ERROR_LEVELS,
  };
})();
